import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import {
  AnimatePresence,
  motion,
  type Transition,
  useReducedMotion,
  type Variants
} from 'framer-motion';
import { cn, getTestId } from '../utils';
import { MenuWrapperItem } from '../types';
import {
  Avatar,
  Button,
  Divider,
  Dropdown,
  DropdownMenu,
  Icon,
  Label,
  Logo,
  MenuButton,
  MenuToggleIcon,
  MenuWrapper
} from '../components';
import {
  ProfileAccountSelect,
  ProfileInvitationsPanel,
  ProfileMenu,
  type ProfileMenuAccounts,
  type ProfileMenuEntry,
  type ProfileMenuInvitations,
  type ProfileMenuUsage,
  ProfileUsagePanel
} from './ProfileMenu';
import { useTranslation } from '../providers/UIProvider';

const MenuVariants = cva(`grid gap-0`, {
  variants: {
    orientation: {
      horizontal:
        "grid-cols-[auto_1fr_auto] [grid-template-areas:'logo_items_profile'] py-sm border-b border-b-border-base",
      vertical: "grid-rows-[auto_1fr_auto] [grid-template-areas:'logo'_'items'_'profile']"
    }
  },
  defaultVariants: {
    orientation: 'vertical'
  }
});

type MenuVariantProps = VariantProps<typeof MenuVariants>;

/**
 * Surface of the bar. `masterAccountBoard` marks the master-account board, which sits above the app, so
 * it gets its own tinted surface; `default` keeps the plain app bar.
 */
const menuSurfaceVariants = cva('', {
  variants: {
    variant: {
      default: 'border-b-border-base',
      masterAccountBoard: 'bg-info-bg border-b-info-border'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});

const menuLabelVariants = cva('max-w-48 truncate text-sm lg:max-w-96', {
  variants: {
    variant: {
      default: 'font-medium text-foreground-muted',
      masterAccountBoard: 'font-semibold text-info-text'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});

export type MenuVariant = NonNullable<VariantProps<typeof menuSurfaceVariants>['variant']>;

// Mobile drawer contents: entries fade in one after another behind the panel.
const drawerListVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.05, delayChildren: 0.08 } }
};

const drawerItemVariants: Variants = {
  hidden: { opacity: 0, x: 24 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] } }
};

export interface MenuProps extends MenuVariantProps {
  className?: string;
  /** `masterAccountBoard` gives the bar the master-account board surface; `default` is the app bar. */
  variant?: MenuVariant;
  /** Shown next to the logo in place of the active account's name, e.g. the master account name. */
  label?: React.ReactNode;
  testId?: string;
  id?: string;
  showLogo?: boolean;
  showProfile?: boolean;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  itemsOrientation?: 'horizontal' | 'vertical' | 'sidebar';
  user?: {
    name?: string | null;
    email?: string | null;
    image?: string | null;
  };
  /** Extraction quota panel shown in the desktop profile menu. */
  usage?: ProfileMenuUsage | null;
  /** Account switcher shown in the desktop profile menu when there is more than one. */
  accounts?: ProfileMenuAccounts | null;
  /** Pending invitations, each joinable with one click. */
  invitations?: ProfileMenuInvitations | null;
  logout?: () => void;
  onMyAccountClick?: () => void;
  items: MenuWrapperItem[];
  onSystemSettingsClick?: () => void;
  onLogoClick?: () => void;
  languageSelector?: React.ReactNode;
  isSettingsActive?: boolean;
  isProfileActive?: boolean;
  /** Router link component (e.g. react-router `Link`) used to render nav entries as SPA links. */
  linkComponent?: React.ElementType;
  /** Route targets — when set, the matching entry renders as a link instead of a click handler. */
  logoHref?: string;
  myAccountHref?: string;
  systemSettingsHref?: string;
  /** Code lists of System settings, listed under it; omitted renders nothing. */
  codeListsHref?: string;
  /** Automation of System settings, listed under it; omitted renders nothing. */
  automationHref?: string;
  policiesHref?: string;
}

export const Menu = React.forwardRef<HTMLDivElement, MenuProps>(
  (
    {
      className,
      variant = 'default',
      label,
      testId,
      id,
      items,
      orientation = 'vertical',
      showLogo,
      showProfile,
      header,
      footer,
      itemsOrientation,
      user,
      usage,
      accounts,
      invitations,
      logout,
      onMyAccountClick,
      onSystemSettingsClick,
      onLogoClick,
      languageSelector,
      isSettingsActive,
      isProfileActive,
      linkComponent,
      logoHref,
      myAccountHref,
      systemSettingsHref,
      codeListsHref,
      automationHref,
      policiesHref
    },
    ref
  ) => {
    const t = useTranslation('modules.menu');

    // Build polymorphic link props for a nav entry: SPA link when a router
    // `linkComponent` is provided, native anchor with just `href`, else `{}`.
    const navProps = (href?: string) =>
      href ? (linkComponent ? { as: linkComponent, to: href } : { href }) : {};

    // Render a mobile drawer clickable (link when `href` is set, else button)
    // wrapping arbitrary content.
    const renderMobileClickable = (opts: {
      content: React.ReactNode;
      href?: string;
      onClick?: () => void;
      className?: string;
      ariaLabel?: string;
    }) => {
      const { content, href, onClick, className, ariaLabel } = opts;
      const close = () => setIsMobileMenuOpen(false);

      if (href) {
        const LinkComponent = linkComponent ?? 'a';
        const linkProps = linkComponent ? { to: href } : { href };
        return (
          <LinkComponent
            {...linkProps}
            onClick={close}
            aria-label={ariaLabel}
            className={className}
          >
            {content}
          </LinkComponent>
        );
      }

      return (
        <button
          onClick={() => {
            onClick?.();
            close();
          }}
          aria-label={ariaLabel}
          className={className}
        >
          {content}
        </button>
      );
    };
    const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
    const [isMobileView, setIsMobileView] = React.useState(false);
    const [isSettingsExpanded, setIsSettingsExpanded] = React.useState(false);

    // Switching only makes sense with somewhere to switch to.
    const switchableAccounts = accounts && accounts.items.length > 1 ? accounts : null;
    // Account the session acts in, named next to the logo so the tenant is visible without
    // opening anything. Only when the user has more than one — otherwise it is noise.
    const activeAccountName = switchableAccounts?.items.find(
      account => account.id === switchableAccounts.activeId
    )?.name;

    // Logo-adjacent label, shared by the desktop bar and the mobile bar. An explicit `label`
    // wins over the active account's name.
    const labelContent = label ?? activeAccountName;
    const accountLabel = labelContent && (
      <>
        <Divider
          orientation='vertical'
          variant='light'
          thickness='sm'
          size='full'
          className='h-5 shrink-0'
        />
        <span className={menuLabelVariants({ variant })} data-slot='menu-label'>
          {labelContent}
        </span>
      </>
    );
    const surfaceClassName = menuSurfaceVariants({ variant });
    const rootTestId = getTestId({ testId, id });

    const reduceMotion = useReducedMotion() ?? false;

    const drawerTransition: Transition = reduceMotion
      ? { duration: 0 }
      : { type: 'spring', damping: 30, stiffness: 300, mass: 0.9 };

    const backdropTransition: Transition = { duration: reduceMotion ? 0 : 0.25, ease: 'easeOut' };

    const isDrawerOpen = isMobileView && isMobileMenuOpen;

    React.useEffect(() => {
      const checkViewport = () => {
        const isMobile = window.innerWidth < 768; // md breakpoint is 768px
        setIsMobileView(isMobile);
        // Leaving mobile unmounts the drawer — drop its state with it.
        if (!isMobile) setIsMobileMenuOpen(false);
      };

      // Check on mount
      checkViewport();

      // Listen for resize events
      window.addEventListener('resize', checkViewport);
      return () => window.removeEventListener('resize', checkViewport);
    }, []);

    // While the drawer is open: close on Escape and freeze the page behind it.
    React.useEffect(() => {
      if (!isDrawerOpen) return;

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') setIsMobileMenuOpen(false);
      };
      const previousOverflow = document.body.style.overflow;

      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);

      return () => {
        document.body.style.overflow = previousOverflow;
        window.removeEventListener('keydown', handleKeyDown);
      };
    }, [isDrawerOpen]);

    const dividerOrientation = orientation === 'vertical' ? 'horizontal' : 'vertical';

    // Settings entries listed inside the desktop profile menu.
    const profileMenuEntries: ProfileMenuEntry[] = [
      ...(onSystemSettingsClick || systemSettingsHref
        ? [
            {
              icon: 'gear' as const,
              label: t('systemSettings'),
              href: systemSettingsHref,
              onClick: onSystemSettingsClick
            }
          ]
        : []),
      ...(codeListsHref
        ? [{ icon: 'list-ul' as const, label: t('codeLists'), href: codeListsHref }]
        : []),
      ...(automationHref
        ? [{ icon: 'bolt' as const, label: t('automation'), href: automationHref }]
        : []),
      ...(policiesHref
        ? [{ icon: 'file-signature' as const, label: t('policies'), href: policiesHref }]
        : [])
    ];

    // The drawer body holds nav items plus the settings expander — either is enough
    // to render it.
    const hasDrawerEntries = (items && items.length > 0) || profileMenuEntries.length > 0;

    // For horizontal orientation, use the new layout
    if (orientation === 'horizontal') {
      return (
        <>
          {/* Desktop Menu - only show on desktop */}
          {!isMobileView && (
            <section
              id={id}
              data-testid={rootTestId}
              data-variant={variant}
              className={cn(
                'grid grid-cols-[auto_1fr_auto] items-center px-md py-md border-b gap-md',
                surfaceClassName,
                className
              )}
              ref={ref}
            >
              {/* Left: Logo and the account the session acts in */}
              <div className='flex min-w-0 items-center gap-sm'>
                {showLogo && (
                  <div className='flex items-center'>
                    <Logo
                      {...navProps(logoHref)}
                      className={cn('h-8 w-auto', (onLogoClick || logoHref) && 'cursor-pointer')}
                      onClick={onLogoClick}
                    />
                  </div>
                )}
                {accountLabel}
              </div>

              {/* Center: Menu items - aligned to right */}
              <div className='flex items-center justify-end'>
                <MenuWrapper
                  items={items}
                  orientation={orientation}
                  className={variant === 'masterAccountBoard' ? 'bg-transparent' : undefined}
                />
              </div>

              {/* Right: profile menu with separator */}
              <div className='flex items-center gap-md'>
                <Divider
                  orientation='vertical'
                  variant='light'
                  thickness='sm'
                  size='full'
                  className='h-6'
                />

                {showProfile && (
                  <ProfileMenu
                    user={user}
                    accounts={accounts}
                    invitations={invitations}
                    usage={usage}
                    entries={profileMenuEntries}
                    myAccountHref={myAccountHref}
                    onMyAccountClick={myAccountHref ? undefined : onMyAccountClick}
                    logout={logout}
                    linkComponent={linkComponent}
                  />
                )}

                {/* Language selector - at the end */}
                {languageSelector && <div className='flex items-center'>{languageSelector}</div>}
              </div>
            </section>
          )}

          {/* Mobile Menu - only show on mobile */}
          {isMobileView && (
            <section
              id={id}
              data-testid={rootTestId}
              data-variant={variant}
              className={cn(
                'flex items-center justify-between px-md py-sm border-b gap-sm',
                surfaceClassName
              )}
            >
              {/* Left: Logo and the account the session acts in */}
              <div className='flex min-w-0 items-center gap-sm'>
                {showLogo && (
                  <div className='flex items-center'>
                    <Logo
                      {...navProps(logoHref)}
                      className={cn('h-8 w-auto', (onLogoClick || logoHref) && 'cursor-pointer')}
                      onClick={onLogoClick}
                    />
                  </div>
                )}
                {accountLabel}
              </div>

              {/* Right: Hamburger and icons */}
              <div className='flex shrink-0 items-center gap-md'>
                {/* Hamburger menu button */}
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className='p-sm hover:bg-background-muted rounded-lg transition-colors text-foreground-base'
                  aria-label='Menu'
                  aria-expanded={isMobileMenuOpen}
                  data-onboarding-mobile='burger'
                >
                  <MenuToggleIcon open={isMobileMenuOpen} />
                </button>
              </div>
            </section>
          )}

          {/* Mobile Menu Overlay - only show on mobile */}
          <AnimatePresence>
            {isDrawerOpen && (
              /* Backdrop */
              <motion.div
                key='mobile-menu-backdrop'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={backdropTransition}
                className='fixed inset-0 bg-overlays-50 z-60 md:hidden'
                onClick={() => setIsMobileMenuOpen(false)}
              />
            )}
            {isDrawerOpen && (
              /* Sidebar */
              <motion.div
                key='mobile-menu-drawer'
                role='dialog'
                aria-label='Menu'
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={drawerTransition}
                className='fixed top-0 right-0 h-full w-80 bg-background-input shadow-lg z-70 md:hidden'
              >
                <div className='flex flex-col h-full'>
                  {/* Header */}
                  <div
                    className={cn(
                      'flex items-center justify-between p-md border-b',
                      surfaceClassName
                    )}
                  >
                    {showLogo && (
                      <Logo
                        {...navProps(logoHref)}
                        className={cn('h-8 w-auto', (onLogoClick || logoHref) && 'cursor-pointer')}
                        onClick={() => {
                          onLogoClick?.();
                          setIsMobileMenuOpen(false);
                        }}
                      />
                    )}
                    <button
                      onClick={() => setIsMobileMenuOpen(false)}
                      className='p-sm hover:bg-background-muted rounded-lg transition-colors text-foreground-base'
                      aria-label='Close menu'
                    >
                      <MenuToggleIcon open />
                    </button>
                  </div>

                  {/* Menu Items */}
                  <div className='flex-1 overflow-y-auto p-md'>
                    {hasDrawerEntries && (
                      <motion.div
                        className='mb-md'
                        variants={drawerListVariants}
                        initial={reduceMotion ? false : 'hidden'}
                        animate='visible'
                      >
                        {items?.map((item, index) => {
                          const itemColor = item.isActive
                            ? 'text-secondary-base'
                            : 'text-foreground-base';
                          return (
                            <motion.div key={index} variants={drawerItemVariants}>
                              {item.href ? (
                                (() => {
                                  const LinkComponent = linkComponent ?? 'a';
                                  const linkProps = linkComponent
                                    ? { to: item.href }
                                    : { href: item.href };
                                  return (
                                    <LinkComponent
                                      {...linkProps}
                                      onClick={() => setIsMobileMenuOpen(false)}
                                      className='w-full flex items-center gap-md px-md py-md rounded-lg hover:bg-background-muted transition-colors text-left'
                                    >
                                      {item.icon && <Icon name={item.icon} className={itemColor} />}
                                      <span className={cn('font-medium', itemColor)}>
                                        {item.label}
                                      </span>
                                    </LinkComponent>
                                  );
                                })()
                              ) : (
                                <button
                                  onClick={() => {
                                    item.onClick?.();
                                    setIsMobileMenuOpen(false);
                                  }}
                                  disabled={item.disabled}
                                  className='w-full flex items-center gap-md px-md py-md rounded-lg hover:bg-background-muted transition-colors text-left disabled:opacity-50'
                                >
                                  {item.icon && <Icon name={item.icon} className={itemColor} />}
                                  <span className={cn('font-medium', itemColor)}>{item.label}</span>
                                </button>
                              )}
                            </motion.div>
                          );
                        })}
                        {profileMenuEntries.length > 0 && (
                          <motion.div variants={drawerItemVariants}>
                            <Button
                              variant='ghost'
                              size='full'
                              onClick={() => setIsSettingsExpanded(prev => !prev)}
                              aria-expanded={isSettingsExpanded}
                              className={cn(
                                '[&>span]:flex [&>span]:w-full [&>span]:items-center [&>span]:gap-md h-auto rounded-lg px-md py-md text-left',
                                isSettingsActive ? 'text-secondary-base' : 'text-foreground-base'
                              )}
                            >
                              <Icon name='gear' className='shrink-0' />
                              <span className='font-medium flex-1'>{t('settings')}</span>
                              <Icon
                                name='chevron-down'
                                className={cn(
                                  'h-4 w-4 shrink-0 text-foreground-muted transition-transform duration-200',
                                  isSettingsExpanded && 'rotate-180'
                                )}
                              />
                            </Button>

                            <div
                              className={cn(
                                'grid transition-[grid-template-rows] duration-200 ease-out',
                                isSettingsExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                              )}
                            >
                              <div className='overflow-hidden'>
                                <div className='flex flex-col gap-2xs pl-lg pt-2xs'>
                                  {profileMenuEntries.map(entry => (
                                    <React.Fragment key={entry.label}>
                                      {renderMobileClickable({
                                        href: entry.href,
                                        onClick: entry.onClick,
                                        ariaLabel: entry.label,
                                        className:
                                          'w-full flex items-center gap-md px-md py-sm rounded-lg hover:bg-background-muted transition-colors text-left text-foreground-base',
                                        content: (
                                          <>
                                            <Icon name={entry.icon} />
                                            <span className='font-medium'>{entry.label}</span>
                                          </>
                                        )
                                      })}
                                    </React.Fragment>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </motion.div>
                    )}
                  </div>

                  {/* Organizations, above the quota block */}
                  {switchableAccounts && (
                    <ProfileAccountSelect
                      accounts={switchableAccounts}
                      onAfterSelect={() => setIsMobileMenuOpen(false)}
                      className='border-t border-t-border-base'
                    />
                  )}

                  {/* Pending invitations, right below the organizations they would join */}
                  {invitations && invitations.items.length > 0 && (
                    <ProfileInvitationsPanel
                      invitations={invitations}
                      onAfterSelect={() => setIsMobileMenuOpen(false)}
                      className='border-t border-t-border-base'
                    />
                  )}

                  {/* Extraction quota, above the profile row */}
                  {showProfile && usage && (
                    <ProfileUsagePanel
                      usage={usage}
                      linkComponent={linkComponent}
                      onNavigate={() => setIsMobileMenuOpen(false)}
                      className='border-t border-t-border-base'
                    />
                  )}

                  {/* User profile: clickable row (→ My Account) + logout icon */}
                  {showProfile && (
                    <div className='flex items-center gap-xs border-t border-t-border-base px-md py-sm'>
                      {renderMobileClickable({
                        href: myAccountHref,
                        onClick: onMyAccountClick,
                        ariaLabel: t('myAccount'),
                        className: cn(
                          'flex flex-1 min-w-0 items-center gap-md rounded-lg px-md py-sm text-left transition-colors',
                          (onMyAccountClick || myAccountHref) && 'hover:bg-background-muted',
                          isProfileActive && 'text-secondary-base'
                        ),
                        content: (
                          <>
                            <Avatar src={user?.image ?? undefined} name={user?.name ?? undefined} />
                            <div className='flex flex-col min-w-0'>
                              <Label size='md' className='h-6 font-medium truncate'>
                                {user?.name ?? 'username'}
                              </Label>
                              <Label size='xs' className='text-foreground-muted truncate'>
                                {user?.email ?? 'email'}
                              </Label>
                            </div>
                          </>
                        )
                      })}

                      <Button
                        orientation='vertical'
                        variant='ghost'
                        icon='arrow-right-from-bracket'
                        onClick={() => {
                          logout?.();
                          setIsMobileMenuOpen(false);
                        }}
                        aria-label={t('logout')}
                        className='shrink-0 h-auto gap-xs rounded-lg px-sm py-xs text-error-text hover:bg-error-bg'
                      >
                        <span className='text-xs font-medium'>{t('logout')}</span>
                      </Button>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      );
    }

    // Original vertical layout
    return (
      <section
        id={id}
        data-testid={rootTestId}
        data-variant={variant}
        className={cn(
          MenuVariants({ orientation }),
          variant === 'masterAccountBoard' && surfaceClassName,
          className
        )}
        ref={ref}
      >
        {header ? (
          <div className={cn('[grid-area:logo]')}>{header}</div>
        ) : showLogo ? (
          <div className={cn('[grid-area:logo] max-w-56 flex', label && 'items-center')}>
            <Logo
              className={cn(
                orientation === 'vertical' ? 'my-xl mx-md' : 'my-lg mx-sm',
                onLogoClick && 'cursor-pointer'
              )}
              onClick={onLogoClick}
            />
            {label && (
              <span className={menuLabelVariants({ variant })} data-slot='menu-label'>
                {label}
              </span>
            )}
          </div>
        ) : null}
        <div className={cn('[grid-area:items]')}>
          <MenuWrapper
            items={items}
            orientation={itemsOrientation ?? orientation}
            className={variant === 'masterAccountBoard' ? 'bg-transparent' : undefined}
          />
        </div>
        {footer ? (
          <div className={cn('[grid-area:profile] mt-auto')}>{footer}</div>
        ) : showProfile ? (
          <div
            className={cn(
              '[grid-area:profile]',
              orientation === 'vertical' ? 'mt-auto' : 'ml-auto flex'
            )}
          >
            <Divider orientation={dividerOrientation} variant='light' thickness='sm' />

            <DropdownMenu
              align='bottom end'
              className='-mt-7.5!'
              triggerButton={
                <MenuButton
                  icon={<Avatar src={user?.image ?? undefined} name={user?.name ?? undefined} />}
                  orientation={orientation}
                  variant='inactive'
                  className='mx-md'
                >
                  {user?.name ?? t('profile')}
                </MenuButton>
              }
              HeadItem={
                <div className='flex flex-row gap-md min-w-0'>
                  <Avatar src={user?.image ?? undefined} name={user?.name ?? undefined} />
                  <div className='flex flex-col gap-0 min-w-0'>
                    <Label size='md' className='h-5 font-medium truncate'>
                      {user?.name ?? 'username'}
                    </Label>
                    <Label size='xs' className='text-foreground-muted truncate'>
                      {user?.email ?? 'email'}
                    </Label>
                  </div>
                </div>
              }
            >
              {onMyAccountClick && (
                <Dropdown.Item icon='user' onClick={onMyAccountClick} className='m-sm'>
                  {t('myAccount')}
                </Dropdown.Item>
              )}
              <Dropdown.Item
                icon='arrow-right-from-bracket'
                onClick={logout}
                className='text-error-text m-sm hover:bg-error-bg'
              >
                {t('logout')}
              </Dropdown.Item>
            </DropdownMenu>
          </div>
        ) : null}
      </section>
    );
  }
);

Menu.displayName = 'Menu';
